import { useAppDispatch } from '@/app/store/hooks';
import { useQuote } from '../context/QuoteContext';
import { resetFromStep } from '../store/quoteSlice';
import { firstIncompleteStepBefore, navigationBlockedMessage, goToStepLabel } from '../utils/stepGate';

const CHOOSE_ANOTHER_PRODUCT_LABEL = 'Change product';

export interface ChooseAnotherProductRecovery {
    /** Why the Product step cannot be reached. `null` when it can. */
    message: string | null;
    /**
     * The single primary control. `null` ONLY in the defensive case where the blocking step IS the
     * step being displayed — routing there would be visibly inert, i.e. the very defect this fixes.
     * Never a dead button.
     */
    action: { label: string; onClick: () => void } | null;
}

// Never returns a dead control: when the Product step is blocked it routes to the earliest blocker
// without dispatching `resetFromStep`, so a refused navigation cannot destroy journey data.
export const useChooseAnotherProduct = (): ChooseAnotherProductRecovery => {
    const dispatch = useAppDispatch();
    const { stepOrder, currentStep, isStepValid, goToStep } = useQuote();

    const blockingStep = firstIncompleteStepBefore(stepOrder, 'product', isStepValid);

    if (blockingStep === null) {
        return {
            message: null,
            action: {
                label: CHOOSE_ANOTHER_PRODUCT_LABEL,
                onClick: () => {
                    dispatch(resetFromStep({ step: 'configuration', order: stepOrder }));
                    goToStep('product');
                },
            },
        };
    }

    // The blocking step is already on screen, so no navigation can help: suppress both the
    // control (it would be inert) and the message (it would point at the step being shown).
    if (blockingStep === currentStep) {
        return { message: null, action: null };
    }

    return {
        message: navigationBlockedMessage(blockingStep, 'product'),
        action: { label: goToStepLabel(blockingStep), onClick: () => goToStep(blockingStep) },
    };
};
